<template>
    <div class="w-100 h-100">
        <!-- MODALS -->
        <!-- https://bootstrap-vue.org/docs/components/modal -->
        <!-- This modal shows if there is a problem with the API call. -->
        <b-modal
            id="api-err-modal"
            v-model="showApiErrModal"
            centered
            scrollable
            hide-footer
            hide-header-close
            no-close-on-backdrop
            no-close-on-esc
            title="Dataset Fetch Error"
            :header-bg-variant="'warning'"
            :header-text-variant="'light'">
            <span v-html="err_msg"></span>
            <b-button class="mt-3 button-link" :to="{name: 'blood-sample-review'}" block>Return to Blood Sample Review</b-button>
        </b-modal>
        <b-modal
            id="submit-modal"
            v-model="showSubmitModal"
            centered
            scrollable
            title="Submit Blood Sample Analysis Changes?"
            :header-bg-variant="'warning'"
            :header-text-variant="'light'"
            @ok="submitBSA"
        >
            <div class="text-center">
                <div>Are you sure you want to submit these changes?</div>
            </div>
        </b-modal>
        <b-modal
            v-model="showModal"
            hide-header
            hide-header-close
            centered
            no-close-on-backdrop
            @ok="OKClick"
        >
            <div v-if="ModalHTMLMessage != ''" v-html="ModalHTMLMessage"></div>
            <div v-else class="text-center">
                <div>{{ModalMessage}}</div>
            </div>
        </b-modal>
        <!-- This modal shows when the curator clicks the "Archive" button. -->
        <b-modal
            id="archive-bsa-modal"
            v-model="showArchiveBSAModal"
            centered
            scrollable
            no-close-on-backdrop
            title="Confirm Blood Sample Analysis Archival"
            ok-title="Yes, archive Blood Sample Analysis"
            @show="resetModal"
            @hidden="resetModal"
            @ok="handleOk"
            :header-bg-variant="'danger'"
            :header-text-variant="'light'"
        >
            <!-- <p class="my-4">{{modalText}}</p> -->
            <span v-html="'Are you sure you want to archive this blood sample analysis?'"></span>
            <form ref="form" @submit.stop.prevent="handleArchiveSubmit">
            <b-form-group
                invalid-feedback="Confirmation is required"
                :state="deleteConfirmState"
            >
            <br>
            <b-form-checkbox
                id="delete-confirm-input"
                name="delete-confirm-chkbx"
                v-model="deleteConfirmed"
            >I'm sure.</b-form-checkbox>
            </b-form-group>
        </form>
        </b-modal>
        <!-- This modal shows while the dataset is being deleted. -->
        <b-modal
            id="warning-modal"
            v-model="showDeleteWorkingModal"
            centered
            scrollable
            hide-footer
            hide-header-close
            no-close-on-backdrop
            no-close-on-esc
            title="Archiving Blood Sample Analysis"
            :header-bg-variant="'warning'"
            :header-text-variant="'light'"
        >
            <span v-html="'Please wait while the blood sample analysis is archived.'"></span>
        </b-modal>
        
        <b-container fluid>
            <b-row>
                <b-col>
                    <h2 class="tw-mb-0 text-aegis-dk">Edit Blood Sample</h2>
                    <p class="tw-italic tw-text-gray-700">
                        Edit blood sample analysis details. Note that all changes are logged.
                    </p>
                </b-col>
                <b-col>
                    <div style="float: right;">
                        <b-button 
                            class="ml-1 button-link" 
                            :to="{name: 'blood-sample-review'}" 
                            variant="primary" 
                            size="sm" 
                            block
                        >
                            Return to Blood Sample Review
                        </b-button>
                        <b-button 
                            class="ml-1 button-link" 
                            variant="danger" 
                            size="sm" 
                            block
                            @click="showArchiveBSAModal = true"
                        >
                            Archive This Blood Sample Analysis
                        </b-button>
                    </div>
                </b-col>
            </b-row>
        </b-container>
        <br />

        <b-container fluid>
            <b-card bg-variant="light" class="tw-mx-3">
                <b-row>
                    <b-col cols="12">
                        <h3>Blood Sample ID: <b>{{ blood_sample_id }}</b></h3>
                        <p>
                            If the Blood Sample ID is incorrect and you HAVE NOT "received" it in the portal, please 
                            correct the barcode in the 
                            <span v-if="cbs_id != null">
                                <a :href="case_link" target="_blank">Commcare Case (not in the form)</a>.
                            </span>
                            <span v-else>
                                Commcare Case (not in the form).
                            </span>
                        </p>
                        <p>
                            If you HAVE "received" the Blood Sample ID, please archive this record here in the portal, 
                            then follow the steps above to make your correction in Commcare immediately.  You will 
                            need to receive this new Blood Sample ID and re-enter any data associated with it (IgG 
                            ELISA, etc).
                        </p>
                        <p>
                            Corrected records should be updated in the portal after the next full synchronization with 
                            Commcare (within ~3 hours).
                        </p>
                        <!-- <p v-if="cbs_id != null"><a :href="case_link" target="_blank">Case Link</a></p>
                        <p v-else>(Blood Sample case could not be automatically found by portal.)</p> -->
                        <!-- <h6>Received on: {{ received_on }}</h6> -->
                        <!-- <h6>Compound: {{ compound_member_id }}</h6> -->
                        <!-- <h6>Sample Type: {{ sample_type }}</h6> -->
                        <!-- <h6>Study Phase: {{ study_phase }}</h6> -->
                        <!-- <h6>Subject ID: {{ subject_id }}</h6> -->
                        <!-- <h6>Visit Type: {{ visit_type }}</h6> -->
                        <!-- <h6>MOH: {{ moh }}</h6> -->
                        <br />
                    </b-col>
                </b-row>

                <small v-for="(aliquot, index) in aliquots" :key="index">
                    <b-row class="mt-2">
                        <b-col>
                        <h6>Aliquot {{ index + 1 }}</h6>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                        <b-form-group
                            :id="'aliquot' + (index + 1) + '_id'"
                            :label="'ID'"
                            label-cols-sm="1"
                            label-class="text-sm-right"
                        >
                            <b-form-input
                            v-model="aliquot.id"
                            ></b-form-input>
                        </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                        <b-form-group
                            :id="'aliquot' + (index + 1) + '_box_num'"
                            :label="'Box Number'"
                            label-cols-sm="6"
                            label-class="text-sm-right"
                        >
                            <b-form-input
                            v-model="aliquot.box_num"
                            ></b-form-input>
                        </b-form-group>
                        </b-col>
                        <b-col>
                        <b-form-group
                            :id="'aliquot' + (index + 1) + '_freezer_num'"
                            :label="'Freezer Number'"
                            label-cols-sm="6"
                            label-class="text-sm-right"
                        >
                            <b-form-input
                            v-model="aliquot.freezer_num"
                            ></b-form-input>
                        </b-form-group>
                        </b-col>
                        <b-col>
                        <b-form-group
                            :id="'aliquot' + (index + 1) + '_shelf_num'"
                            :label="'Shelf Number'"
                            label-cols-sm="6"
                            label-class="text-sm-right"
                        >
                            <b-form-input
                            v-model="aliquot.shelf_num"
                            ></b-form-input>
                        </b-form-group>
                        </b-col>
                    </b-row>
                </small>

                <b-row class="mt-2">
                    <b-col>
                    <h6>IgG ELISA</h6>
                    </b-col>
                </b-row>
                <small>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                            label-cols-sm="1"
                            id='igg_elisa_od_value_id'
                            :label="'IgG ELISA OD Value'"
                            label-class="text-sm-right"
                        >
                        <b-form-input
                            type="number"
                            v-model.number="igg_elisa_od_value"
                        ></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>
                <!-- <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                            label-cols-sm="4"
                            :id=igg_elisa_result
                            :label="'IgG ELISA Result'"
                        >
                            <b-form-input
                                v-model="igg_elisa_result"
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row> -->

                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="igg_elisa_result_id"
                        :label="'IgG ELISA Result'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="igg_elisa_result"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                </small>
                <b-row class="mt-2">
                    <b-col>
                    <h6>Luminex</h6>
                    </b-col>
                </b-row>
                <small>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="denv1_id"
                        :label="'denv1'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="denv1"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="denv2_id"
                        :label="'denv2'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="denv2"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="denv3_id"
                        :label="'denv3'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="denv3"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="denv4_id"
                        :label="'denv4'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="denv4"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="multi_or_mono_id"
                        :label="'multi_or_mono'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="multi_or_mono"
                            :options="[{value: null, text: 'None'}, 'Monotypic', 'Multitypic']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="zikv_id"
                        :label="'zikv'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="zikv"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-group
                        label-cols-sm="1"
                        id="chikv_id"
                        :label="'chikv'"
                        label-class="text-sm-right"
                        >
                        <b-form-select
                            v-model="chikv"
                            :options="[{value: null, text: 'None'}, 'Positive', 'Negative']"
                        ></b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                </small>
                <b-row>
                    <b-col cols="12">
                        <center>
                            <b-button
                                class="m-2 col-5"
                                variant="success"
                                @click="submitForm()"
                            >
                                Submit
                            </b-button>
                        </center>
                    </b-col>
                </b-row>
            </b-card>
        </b-container>
    </div>
  </template>
  
<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";
import * as _ from "lodash";
import { mapGetters } from 'vuex';
import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";
  
export default {
mixins: [
    DashboardBaseTemplate,
],
components: {},
props: {
    id: {
      type: [Number, String],
      require: true,
    },
  },
watch: {},
data: function() {
    return {
        showApiErrModal: false,
        showModal: false,
        showSubmitModal: false,
        showArchiveBSAModal: false,
        showDeleteWorkingModal: false,
        deleteConfirmState: false,
        deleteConfirmed: false,
        ModalMessage: '',
        err_msg: '',
        ModalHTMLMessage: '',
        // model specific vars:
        blood_sample_id: '',
        received_on: '',
        compound_member_id: '',
        sample_type: '',
        study_phase: '',
        subject_id: '',
        visit_type: '',
        moh: '',
        igg_elisa_od_value: '',
        igg_elisa_result: '',
        denv1: '',
        denv2: '',
        denv3: '',
        denv4: '',
        multi_or_mono: '',
        zikv: '',
        chikv: '',
        cbs_id: null,
        aliquots: [], // holds all aliquot data. Pop'd by getBSA()
    };
},
computed: {
    ...mapGetters("user", ["inMyGroups"]),
    case_link() {
        return `https://www.commcarehq.org/a/aegis-sri-lanka/reports/case_data/${this.cbs_id}/`
    },
},
created: function() {
    if(!this.inMyGroups(['Blood Sample Manager'])) {
        this.err_msg = "You do not have permission to view this page.";
        this.showApiErrModal = true;
    }
},
mounted() {
    this.getBSA();
},
methods: {
    getBSA: function () {
        const that = this;
        const fd = new FormData();
        
        fd.append("id", that.id);
        HTTP.post('srilanka/blood_sample/get_bsa_by_id/', fd).then((response) => {
            if(response.data.message != null) {
                this.ModalMessage = response.data.message;
                this.showModal = true;
            }
            if (response.data.message == null) {
                that.blood_sample_id = response.data.bsa.blood_sample_id;
                that.received_on = response.data.bsa.received_on;
                that.compound_member_id = response.data.bsa.compound_member_id;
                that.sample_type = response.data.bsa.sample_type;
                that.study_phase = response.data.bsa.study_phase;
                that.subject_id = response.data.bsa.subject_id;
                that.visit_type = response.data.bsa.visit_type;
                that.moh = response.data.bsa.moh;
                that.cbs_id = response.data.cbs_id;
                for (let i = 1; i <= 4; i++) {
                    that.aliquots.push({
                        id: response.data.bsa[`aliquot${i}_id`],
                        box_num: response.data.bsa[`aliquot${i}_box_num`],
                        freezer_num: response.data.bsa[`aliquot${i}_freezer_num`],
                        shelf_num: response.data.bsa[`aliquot${i}_shelf_num`],
                    });
                }
                that.igg_elisa_od_value = response.data.bsa.igg_elisa_od_value;
                that.igg_elisa_result = response.data.bsa.igg_elisa_result;
                that.denv1 = response.data.bsa.denv1;
                that.denv2 = response.data.bsa.denv2;
                that.denv3 = response.data.bsa.denv3;
                that.denv4 = response.data.bsa.denv4;
                that.multi_or_mono = response.data.bsa.multi_or_mono;
                that.zikv = response.data.bsa.zikv;
                that.chikv = response.data.bsa.chikv;
            }
        }).catch((error) => {
            HandleErrors.HandleErrors(error);
            this.ModalMessage = 'There was a problem. Please check barcode and try again.';
            this.showModal = true;
        });
    },
    submitForm () {
        this.showSubmitModal = true;
    },
    submitBSA: function() {
        const that = this;
        that.ModalHTMLMessage = '';
        const fd = new FormData();
        fd.append("id", that.id);
        for (let i = 1; i <= 4; i++) {
            fd.append(`aliquot${i}_id`, that.aliquots[i - 1].id);
            fd.append(`aliquot${i}_box_num`, that.aliquots[i - 1].box_num);
            fd.append(`aliquot${i}_freezer_num`, that.aliquots[i - 1].freezer_num);
            fd.append(`aliquot${i}_shelf_num`, that.aliquots[i - 1].shelf_num);
        }
        if(that.igg_elisa_od_value != null && that.igg_elisa_od_value != '') {
            fd.append("igg_elisa_od_value", that.igg_elisa_od_value);
        }
            
        if(that.igg_elisa_result != null && that.igg_elisa_result != '') {
            fd.append("igg_elisa_result", that.igg_elisa_result);
        }
        if(that.denv1 != null && that.denv1 != '') {
            fd.append("denv1", that.denv1);
        }
        if(that.denv2 != null && that.denv2 != '') {
            fd.append("denv2", that.denv2);
        }
        if(that.denv3 != null && that.denv3 != '') {
            fd.append("denv3", that.denv3);
        }
        if(that.denv4 != null && that.denv4 != '') {
            fd.append("denv4", that.denv4);
        }
        if(that.multi_or_mono != null && that.multi_or_mono != '') {
            fd.append("multi_or_mono", that.multi_or_mono);
        }
        if(that.zikv != null && that.zikv != '') {
            fd.append("zikv", that.zikv);
        }
        if(that.chikv != null && that.chikv != '') {
            fd.append("chikv", that.chikv);
        }
        
        this.showSubmitModal = false;
        HTTP.put('srilanka/blood_sample/edit/', fd).then((response) => {
            if(response.data.message != null) {
                that.ModalHTMLMessage = '';
                that.ModalMessage = response.data.message;
                that.showModal = true;
            } else {
                that.ModalHTMLMessage = '';
                that.ModalMessage = 'Submitted successfully';
                that.showModal = true;
            }
        }).catch((error) => {
            HandleErrors.HandleErrors(error);
            that.ModalMessage = 'There was a problem. Please check input values and try again.';
            that.showModal = true;
        });
    },
    OKClick: function() {
        this.showModal = false;
    },
    cancelForm() {
        this.ModalHTMLMessage = '';
        this.showModal = false;
    },
    resetModal() {
      this.deleteConfirmed = ''
      this.deleteConfirmState = null
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault()
      // Trigger submit handler
      this.handleArchiveSubmit()
    },
    handleArchiveSubmit() {
        // Exit when the form isn't valid
        if (!this.deleteConfirmed) {
            this.deleteConfirmState = false;
            return;
        }
        // Hide the modal manually
        this.showArchiveBSAModal = false;
        this.archiveBSA();
    },
    archiveBSA() {
        this.showDeleteWorkingModal = true;
        const fd = new FormData();
        fd.append("id", this.id);
        return HTTP.post(`srilanka/blood_sample/archive/`, fd).then((response) => {
            this.showDeleteWorkingModal = false;
            this.$router.push({name: 'blood-sample-review'})
            return response;
        })
        .catch(error => {
            this.showDeleteWorkingModal = false;
            this.err_msg = HandleErrors.HandleErrors(error);
            this.apiErrModalShow = true;
            return [];
        });
    },
}
};
</script>

<style scoped></style>